/**
 * Emerald Books — Payroll module.
 *
 * Full CRUD:
 *   • Employees list + create + edit + toggle-active
 *   • Payroll runs list + create + drill-down + finalize (posts wage journal)
 *   • Payslip detail (per-employee deductions + net + employer cost)
 *
 * Deductions/statutory calcs happen server-side per country_code
 * (UK PAYE+NI, IN PF+TDS, MY EPF+PCB, KZ IT+PENSION).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Plus, Users, Coins, XCircle, CheckCircle2, Pencil, Search, Send,
  Play, FileText,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { booksClient, errMsg } from './api';

const money = (n, cur = '') => `${(Number(n) || 0).toFixed(2)}${cur ? ' ' + cur : ''}`;
const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const lastOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
};

const COUNTRIES = [
  { code: 'GB', name: 'United Kingdom (PAYE + NI)', currency: 'GBP' },
  { code: 'IN', name: 'India (PF + TDS)', currency: 'INR' },
  { code: 'MY', name: 'Malaysia (EPF + PCB)', currency: 'MYR' },
  { code: 'KZ', name: 'Kazakhstan (IT + Pension)', currency: 'KZT' },
];

/* -------------------------------------------------------------------------- */
/* Employees                                                                   */
/* -------------------------------------------------------------------------- */
const emptyEmployee = () => ({
  employee_code: '', name: '', email: '', designation: '',
  country_code: 'GB', tax_id: '', join_date: today(),
  salary_monthly: 0, currency: 'GBP', bank_account: '', is_active: true,
});

export const PayrollEmployeesPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [form, setForm] = useState(emptyEmployee());
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await booksClient.get('/payroll/employees'); setRows(data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      [r.employee_code, r.name, r.email, r.designation, r.country_code]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(s)),
    );
  }, [rows, q]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await booksClient.post('/payroll/employees', form);
      toast.success('Employee added');
      setShowForm(false); setForm(emptyEmployee()); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="mx-auto max-w-6xl" data-testid="books-payroll-emps">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Employees</h1>
          <p className="mt-1 text-sm text-slate-500">Manage payroll roster. Salary is monthly, in the employee's local currency.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input placeholder="Search…" className="pl-7" value={q} onChange={(e) => setQ(e.target.value)} data-testid="emp-search" />
          </div>
          <Button onClick={() => { setShowForm(!showForm); setEditing(null); setForm(emptyEmployee()); }} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="emp-new-btn">
            <Plus className="mr-1 h-4 w-4" /> Add employee
          </Button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mb-6 rounded-xl border bg-white p-5 shadow-sm" data-testid="emp-form">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Code</div>
              <Input required value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} data-testid="emp-code" />
            </label>
            <label className="text-sm md:col-span-2">
              <div className="mb-1 text-xs text-slate-500">Name</div>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-name" />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Designation</div>
              <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Country</div>
              <select value={form.country_code} onChange={(e) => {
                const c = COUNTRIES.find((x) => x.code === e.target.value);
                setForm({ ...form, country_code: e.target.value, currency: c?.currency || form.currency });
              }} className="w-full rounded-md border px-2 py-1.5 text-sm" data-testid="emp-country">
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Tax ID (NINO / PAN / IC / IIN)</div>
              <Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Email</div>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Join date</div>
              <Input type="date" value={form.join_date} onChange={(e) => setForm({ ...form, join_date: e.target.value })} />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Salary (monthly)</div>
              <Input type="number" step="0.01" value={form.salary_monthly} onChange={(e) => setForm({ ...form, salary_monthly: parseFloat(e.target.value) || 0 })} data-testid="emp-salary" />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Currency</div>
              <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
            </label>
            <label className="text-sm md:col-span-2">
              <div className="mb-1 text-xs text-slate-500">Bank account</div>
              <Input value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} />
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="emp-save">Save</Button>
          </div>
        </form>
      )}

      <div className="rounded-xl border bg-white">
        {loading ? <div className="p-6 text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</div> :
          filtered.length === 0 ? <div className="p-10 text-center text-slate-400">No employees yet. Click "Add employee" to add your first.</div> :
          <table className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-3 text-left">Code</th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Designation</th>
                <th className="p-3 text-left">Country</th>
                <th className="p-3 text-right">Salary/mo</th>
                <th className="p-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.employee_id} className="border-b last:border-b-0" data-testid={`emp-row-${e.employee_code}`}>
                  <td className="p-3 font-mono text-xs">{e.employee_code}</td>
                  <td className="p-3">
                    <div className="font-medium">{e.name}</div>
                    <div className="text-xs text-slate-400">{e.email}</div>
                  </td>
                  <td className="p-3 text-slate-600">{e.designation || '—'}</td>
                  <td className="p-3 text-slate-600">{e.country_code}</td>
                  <td className="p-3 text-right">{money(e.salary_monthly, e.currency)}</td>
                  <td className="p-3">
                    <span className={'rounded-full px-2 py-0.5 text-xs ' + (e.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500')}>
                      {e.is_active ? 'active' : 'inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Payroll Runs                                                                */
/* -------------------------------------------------------------------------- */
export const PayrollRunsPage = () => {
  const [runs, setRuns] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    period_start: firstOfMonth(),
    period_end: lastOfMonth(),
    pay_date: lastOfMonth(),
    employee_ids: [],
  });
  const [openRun, setOpenRun] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, e] = await Promise.all([
        booksClient.get('/payroll/runs'),
        booksClient.get('/payroll/employees'),
      ]);
      setRuns(r.data); setEmployees(e.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const empName = (id) => employees.find((e) => e.employee_id === id)?.name || id;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        period_start: form.period_start,
        period_end: form.period_end,
        pay_date: form.pay_date,
        employee_ids: form.employee_ids.length ? form.employee_ids : null,
      };
      await booksClient.post('/payroll/runs', payload);
      toast.success('Payroll run drafted');
      setShowForm(false);
      setForm({ ...form, employee_ids: [] });
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const finalize = async (id) => {
    if (!confirm('Finalize this run? A wage journal will be posted and the run will be locked.')) return;
    try {
      const { data } = await booksClient.post(`/payroll/runs/${id}/finalize`);
      toast.success(`Journal ${data.journal_no} posted`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const toggleEmp = (eid) => setForm((f) => ({
    ...f,
    employee_ids: f.employee_ids.includes(eid)
      ? f.employee_ids.filter((x) => x !== eid)
      : [...f.employee_ids, eid],
  }));

  return (
    <div className="mx-auto max-w-6xl" data-testid="books-payroll-runs">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Payroll Runs</h1>
          <p className="mt-1 text-sm text-slate-500">Draft a run for a pay period, review payslips, then finalize to post the wage journal.</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="run-new-btn">
          <Plus className="mr-1 h-4 w-4" /> New run
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mb-6 rounded-xl border bg-white p-5 shadow-sm" data-testid="run-form">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Period start</div>
              <Input type="date" required value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="run-period-start" />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Period end</div>
              <Input type="date" required value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="run-period-end" />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Pay date</div>
              <Input type="date" required value={form.pay_date} onChange={(e) => setForm({ ...form, pay_date: e.target.value })} data-testid="run-pay-date" />
            </label>
          </div>
          <div className="mt-3">
            <div className="mb-1 text-xs font-medium text-slate-600">Employees ({form.employee_ids.length ? `${form.employee_ids.length} selected` : 'all active'})</div>
            <div className="max-h-40 overflow-y-auto rounded border bg-slate-50 p-2 text-sm">
              {employees.filter((e) => e.is_active).length === 0 && <div className="text-slate-400">No active employees.</div>}
              {employees.filter((e) => e.is_active).map((e) => (
                <label key={e.employee_id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-white">
                  <input type="checkbox" checked={form.employee_ids.includes(e.employee_id)} onChange={() => toggleEmp(e.employee_id)} />
                  <span className="font-mono text-xs text-slate-500">{e.employee_code}</span>
                  <span>{e.name}</span>
                  <span className="ml-auto text-xs text-slate-500">{money(e.salary_monthly, e.currency)}</span>
                </label>
              ))}
            </div>
            <div className="mt-1 text-xs text-slate-400">Leave empty to include all active employees.</div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="run-save">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} Draft run
            </Button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {loading && <div className="rounded-xl border bg-white p-6 text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</div>}
        {!loading && runs.length === 0 && <div className="rounded-xl border bg-white p-10 text-center text-slate-400">No payroll runs yet.</div>}
        {runs.map((r) => (
          <div key={r.run_id} className="rounded-xl border bg-white p-4 shadow-sm" data-testid={`run-${r.run_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm text-slate-500">Period</div>
                <div className="font-medium">{r.period_start} → {r.period_end}</div>
                <div className="text-xs text-slate-400 mt-1">Pay date: <span className="text-slate-600">{r.pay_date}</span> · <span className={'rounded-full px-2 py-0.5 text-xs ' + (r.status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700')}>{r.status}</span></div>
              </div>
              <div className="text-right text-sm">
                <div className="grid grid-cols-3 gap-4">
                  <div><div className="text-xs uppercase tracking-wider text-slate-400">Gross</div><div className="font-semibold">{money(r.totals?.gross)}</div></div>
                  <div><div className="text-xs uppercase tracking-wider text-slate-400">Tax</div><div className="text-slate-700">{money(r.totals?.tax)}</div></div>
                  <div><div className="text-xs uppercase tracking-wider text-slate-400">Net</div><div className="font-semibold text-emerald-600">{money(r.totals?.net)}</div></div>
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setOpenRun(r)} data-testid={`run-open-${r.run_id}`}>
                <FileText className="mr-1 h-3 w-3" /> View payslips
              </Button>
              {r.status === 'draft' && (
                <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-500" onClick={() => finalize(r.run_id)} data-testid={`run-finalize-${r.run_id}`}>
                  <CheckCircle2 className="mr-1 h-3 w-3" /> Finalize + post journal
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {openRun && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="run-modal">
          <div className="w-full max-w-4xl rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Payslips — {openRun.period_start} to {openRun.period_end}</h2>
                <div className="text-xs text-slate-500">Pay date {openRun.pay_date}</div>
              </div>
              <button onClick={() => setOpenRun(null)} className="text-slate-400 hover:text-slate-700"><XCircle className="h-5 w-5" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto rounded border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="p-3 text-left">Employee</th>
                    <th className="p-3 text-left">Country</th>
                    <th className="p-3 text-right">Gross</th>
                    <th className="p-3 text-left">Deductions</th>
                    <th className="p-3 text-right">Net</th>
                    <th className="p-3 text-right">Employer cost</th>
                  </tr>
                </thead>
                <tbody>
                  {(openRun.payslips || []).map((p) => (
                    <tr key={p.payslip_id} className="border-b last:border-b-0" data-testid={`payslip-${p.employee_code}`}>
                      <td className="p-3">
                        <div className="font-medium">{p.employee_name}</div>
                        <div className="text-xs text-slate-400 font-mono">{p.employee_code}</div>
                      </td>
                      <td className="p-3">{p.country_code}</td>
                      <td className="p-3 text-right font-medium">{money(p.gross)}</td>
                      <td className="p-3 text-xs">
                        {Object.entries(p.deductions || {}).filter(([k]) => k !== 'net').map(([k, v]) => (
                          <div key={k} className="flex justify-between gap-3 text-slate-600">
                            <span className="uppercase text-[10px] text-slate-400">{k}</span>
                            <span>{money(v)}</span>
                          </div>
                        ))}
                      </td>
                      <td className="p-3 text-right font-semibold text-emerald-600">{money(p.net)}</td>
                      <td className="p-3 text-right text-slate-500">{money(p.employer_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setOpenRun(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
